import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import "@/App.css";

// Version management
import { initVersionCheck, shouldForceRefresh, getAppVersion } from "@/utils/versionManager";

// Google Analytics
import { trackPageView, trackScreenView } from "@/services/analyticsService";

// Push Notifications
import NotificationsOnboarding from "@/components/NotificationsOnboarding";
import { requestPushPermission, initializeFirebase, onForegroundMessage } from "@/services/pushNotifications";
import { toast } from "sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import FoodScannerPage from "@/pages/FoodScannerPage";
import NutritionPage from "@/pages/NutritionPage";
import MealPlansPage from "@/pages/MealPlansPage";
import WorkoutsPage from "@/pages/WorkoutsPage";
import ProgressPage from "@/pages/ProgressPage";
import ProfilePage from "@/pages/ProfilePage";
import SocialPage from "@/pages/SocialPage";
import BariatricPage from "@/pages/BariatricPage";
import AuthCallback from "@/pages/AuthCallback";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import PremiumPage from "@/pages/PremiumPage";

// Context
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { PremiumProvider } from "@/context/PremiumContext";
import { PaywallProvider } from "@/context/PaywallContext";

// Components
import { Toaster } from "@/components/ui/sonner";
import BottomNav from "@/components/BottomNav";

// Initialize version check on app load
const wasUpdated = initVersionCheck();
if (wasUpdated) {
  console.log(`[App] Application updated to v${getAppVersion()}, cache cleared`);
  // Force page reload after update to ensure fresh content
  if (shouldForceRefresh()) {
    console.log('[App] Force refreshing data from API');
  }
}

const ProtectedRoute = ({ children }) => {
  const { user, loading, isProcessingAuth } = useAuth();
  const location = useLocation();

  // Show loading while checking auth or processing OAuth
  if (loading || isProcessingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full gradient-primary" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Redirect to onboarding if not completed
  if (!user.onboarding_completed && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading, isProcessingAuth } = useAuth();

  // Show loading while checking auth or processing OAuth
  if (loading || isProcessingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full gradient-primary" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  if (user) {
    if (!user.onboarding_completed) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRouter() {
  const location = useLocation();
  const [showNotificationsOnboarding, setShowNotificationsOnboarding] = useState(false);

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Track page views with Google Analytics
  useEffect(() => {
    const pageTitles = {
      '/': 'Accueil',
      '/login': 'Connexion',
      '/register': 'Inscription',
      '/onboarding': 'Onboarding',
      '/dashboard': 'Tableau de bord',
      '/scanner': 'Scanner',
      '/nutrition': 'Nutrition',
      '/meals': 'Plans repas',
      '/workouts': 'Entraînements',
      '/progress': 'Progrès',
      '/profile': 'Profil',
      '/social': 'Communauté',
      '/bariatric': 'Bariatrique',
      '/premium': 'Premium',
      '/privacy': 'Politique de confidentialité'
    };
    
    const pageTitle = pageTitles[location.pathname] || 'Fat & Slim';
    trackPageView(location.pathname, pageTitle);
    trackScreenView(pageTitle);
  }, [location.pathname]);

  // Check if we should show notifications onboarding (Play Store compliant)
  useEffect(() => {
    // Only show after user is on a protected page (logged in + onboarding complete)
    const protectedPaths = ['/dashboard', '/nutrition', '/meals', '/workouts', '/progress', '/profile', '/social'];
    const isOnProtectedPage = protectedPaths.some(p => location.pathname.startsWith(p));
    
    if (isOnProtectedPage) {
      const hasSeenOnboarding = localStorage.getItem('notifications_onboarding_seen');
      const permissionStatus = 'Notification' in window ? Notification.permission : 'unsupported';
      
      // Show only if: user hasn't seen it AND permission is 'default' (not yet asked)
      if (!hasSeenOnboarding && permissionStatus === 'default') {
        // Small delay for better UX after page load
        const timer = setTimeout(() => setShowNotificationsOnboarding(true), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [location.pathname]);

  // Initialize Firebase for foreground messages
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'granted') {
      initializeFirebase();
      const unsubscribe = onForegroundMessage((payload) => {
        // Show toast for foreground notifications
        toast.info(payload.notification?.title || 'Nouvelle notification', {
          description: payload.notification?.body
        });
      });
      return () => unsubscribe?.();
    }
  }, []);

  // Handle notifications onboarding completion (user clicked "Activer")
  const handleNotificationsActivate = async () => {
    setShowNotificationsOnboarding(false);
    // User explicitly clicked - NOW we can request permission (Play Store compliant)
    const result = await requestPushPermission();
    if (result.success) {
      toast.success('Notifications activées !', {
        description: 'Vous recevrez des rappels personnalisés'
      });
    } else if (result.error === 'Permission denied') {
      toast.info('Notifications désactivées', {
        description: 'Vous pouvez les activer dans les paramètres de votre navigateur'
      });
    }
  };

  // Handle notifications onboarding skip
  const handleNotificationsSkip = () => {
    setShowNotificationsOnboarding(false);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const showBottomNav = ['/dashboard', '/scanner', '/nutrition', '/meals', '/workouts', '/progress', '/profile'].some(
    path => location.pathname.startsWith(path)
  );

  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        
        {/* Protected routes */}
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/scanner" element={<ProtectedRoute><FoodScannerPage /></ProtectedRoute>} />
        <Route path="/nutrition" element={<ProtectedRoute><NutritionPage /></ProtectedRoute>} />
        <Route path="/meals" element={<ProtectedRoute><MealPlansPage /></ProtectedRoute>} />
        <Route path="/workouts" element={<ProtectedRoute><WorkoutsPage /></ProtectedRoute>} />
        <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/social" element={<ProtectedRoute><SocialPage /></ProtectedRoute>} />
        <Route path="/bariatric" element={<ProtectedRoute><BariatricPage /></ProtectedRoute>} />
        <Route path="/premium" element={<ProtectedRoute><PremiumPage /></ProtectedRoute>} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      
      {showBottomNav && <BottomNav />}
      
      {/* Play Store Compliant: Notifications Onboarding Modal */}
      {showNotificationsOnboarding && (
        <NotificationsOnboarding 
          onComplete={handleNotificationsActivate}
          onSkip={handleNotificationsSkip}
        />
      )}
      
      <Toaster position="top-center" richColors />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <PremiumProvider>
            <BrowserRouter>
              <PaywallProvider>
                <AppRouter />
              </PaywallProvider>
            </BrowserRouter>
          </PremiumProvider>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
